import { ListingSubtype, ListingType, type CreateListingDTO } from 'app/types/listing';
import type { SpecificRouteProp } from 'app/types/navigation';
import type { NewScreenParams } from './index';

export function getNewListingDefaults(
  route: SpecificRouteProp<'new'>,
): import('react-hook-form').DefaultValues<CreateListingDTO> {
  return {
    addressIds: route.params?.defaultValues?.addressIds ?? [],
    description: route.params?.defaultValues?.description ?? '',
    end: route.params?.defaultValues?.end ?? null,
    groupIds: route.params?.defaultValues?.groupIds ?? [],
    item: route.params?.defaultValues?.item ?? {},
    start: route.params?.defaultValues?.start ?? null,
    type: route.params?.defaultValues?.type ?? ListingType.offer,
    subtype: route.params?.defaultValues?.subtype ?? ListingSubtype.object,
    serviceCategoryId: route.params?.defaultValues?.serviceCategoryId ?? null,
    picture: null,
    pictures: [],
    pictureUrl: route.params?.defaultValues?.pictureUrl ?? null,
    customDistance: route.params?.defaultValues?.customDistance ?? null,
    customCenterLatitude: route.params?.defaultValues?.customCenterLatitude ?? null,
    customCenterLongitude: route.params?.defaultValues?.customCenterLongitude ?? null,
    categoryPrimaryId: route.params?.defaultValues?.categoryPrimaryId ?? null,
    quantity: route.params?.defaultValues?.quantity ?? null,
    estimatedPrice: route.params?.defaultValues?.estimatedPrice ?? null,
    price: route.params?.defaultValues?.price ?? null,
    inventory: route.params?.defaultValues?.inventory ?? null,
    urgent: route.params?.defaultValues?.urgent ?? false,
    transactionMode: route.params?.defaultValues?.transactionMode ?? null,
    loanFee: route.params?.defaultValues?.loanFee ?? null,
    loanFeeDescription: route.params?.defaultValues?.loanFeeDescription ?? null,
    loanFeePaymentMethod: route.params?.defaultValues?.loanFeePaymentMethod ?? null,
    loanFeeDocument: route.params?.defaultValues?.loanFeeDocument ?? null,
    timeStart: route.params?.defaultValues?.timeStart ?? null,
    timeEnd: route.params?.defaultValues?.timeEnd ?? null,
    address: route.params?.defaultValues?.address ?? null,
    maxParticipants: route.params?.defaultValues?.maxParticipants?.toFixed(0) ?? null,
    automaticMessage: route.params?.defaultValues?.automaticMessage ?? '',
    duplicates: route.params?.defaultValues?.duplicates ?? null,
  };
}

export function mergeForwardedListingDefaults(
  current: CreateListingDTO,
  requested: NonNullable<NewScreenParams['defaultValues']>,
  defaults: ReturnType<typeof getNewListingDefaults>,
  canPublishDonationOffers: boolean,
) {
  const values = Object.fromEntries(Object.entries(defaults).filter(([key]) => key in requested));
  const next = { ...current, ...values };
  if (requested.type && requested.type !== current.type) {
    if (
      (next.subtype === ListingSubtype.service &&
        (requested.subtype === undefined || next.type === ListingType.offer)) ||
      (next.type === ListingType.offer && next.subtype === ListingSubtype.donation && !canPublishDonationOffers)
    ) {
      next.subtype = ListingSubtype.object;
      next.serviceCategoryId = null;
      next.categoryPrimaryId = requested.categoryPrimaryId ?? null;
    }
    next.duplicates = requested.duplicates ?? null;
    next.automaticMessage = requested.automaticMessage ?? '';
  }
  if (next.subtype !== ListingSubtype.service) {
    next.serviceCategoryId = null;
    if (current.subtype === ListingSubtype.service) next.categoryPrimaryId = requested.categoryPrimaryId ?? null;
  }
  return next;
}

export function getListingVisibilityDefaults(
  addresses: { id: string }[],
  groups: { id: string; subtype?: string }[],
  exclusions: { addressIds?: string[]; groupIds?: string[] },
  preserve: { addressIds?: boolean; groupIds?: boolean } = {},
): Partial<Pick<CreateListingDTO, 'addressIds' | 'groupIds'>> {
  const municipalClosed = groups.some((group) => group.subtype === 'municipal_closed');
  const excludedAddresses = new Set(exclusions.addressIds ?? (municipalClosed ? addresses.map(({ id }) => id) : []));
  const excludedGroups = new Set(exclusions.groupIds);
  return {
    ...(!preserve.addressIds && {
      addressIds: addresses.filter(({ id }) => !excludedAddresses.has(id)).map(({ id }) => id),
    }),
    ...(!preserve.groupIds && { groupIds: groups.filter(({ id }) => !excludedGroups.has(id)).map(({ id }) => id) }),
  };
}
